<template>
	<div class="admin-list-section">
		<div class="section-header">
			<h2>{{ title }}</h2>
			<div class="header-actions">
				<slot name="header-actions"></slot>
				<button v-if="showRefresh" class="action-btn secondary" @click="$emit('refresh')" :disabled="loading">
					🔄 Refresh
				</button>
			</div>
		</div>

		<div class="search-box">
			<input
				v-model="searchQuery"
				type="text"
				:placeholder="searchPlaceholder"
				class="search-input"
			/>
		</div>

		<div v-if="loading" class="loading">
			<div class="spinner"></div>
			<p>Loading {{ title.toLowerCase() }}...</p>
		</div>

		<div v-else-if="filteredItems && filteredItems.length > 0" class="items-list">
			<div
				v-for="item in filteredItems"
				:key="item.id"
				class="list-item"
				:class="{ active: selectedId && selectedId === item.id }"
				@click="$emit('select', item)"
			>
				<div class="list-item-content">
					<slot name="item" :item="item">
						<!-- Default item display -->
						<div class="item-info">
							<div class="item-name">{{ item.name || item.title || 'Untitled' }}</div>
						</div>
					</slot>
				</div>
				<div v-if="$slots['item-actions']" class="item-actions" @click.stop>
					<slot name="item-actions" :item="item"></slot>
				</div>
			</div>
		</div>

		<div v-else class="empty-state">
			{{ emptyMessage }}
		</div>

		<button
			v-if="showNewButton"
			class="action-btn primary full-width mt-10"
			@click="$emit('new')"
		>
			+ {{ newButtonText }}
		</button>
	</div>
</template>

<script>
export default {
	name: 'AdminListSection',
	props: {
		title: {
			type: String,
			required: true
		},
		items: {
			type: Array,
			default: () => []
		},
		selectedId: {
			type: [String, Number],
			default: null
		},
		loading: {
			type: Boolean,
			default: false
		},
		searchPlaceholder: {
			type: String,
			default: 'Search...'
		},
		emptyMessage: {
			type: String,
			default: 'No items found'
		},
		showNewButton: {
			type: Boolean,
			default: false
		},
		showRefresh: {
			type: Boolean,
			default: true
		},
		newButtonText: {
			type: String,
			default: 'New Item'
		},
		searchFields: {
			type: Array,
			default: () => ['name', 'title']
		}
	},
	emits: ['select', 'refresh', 'new'],
	data() {
		return {
			searchQuery: ''
		};
	},
	computed: {
		filteredItems() {
			if (!this.items) return [];
			if (!this.searchQuery) return this.items;
			const query = this.searchQuery.toLowerCase();
			return this.items.filter(item => {
				return this.searchFields.some(field => {
					const value = this.getNestedValue(item, field);
					return value && value.toString().toLowerCase().includes(query);
				});
			});
		}
	},
	methods: {
		getNestedValue(obj, path) {
			return path.split('.').reduce((current, key) => current?.[key], obj);
		}
	}
};
</script>

<style scoped>
.admin-list-section {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	padding: 20px;
	box-shadow: var(--box-shadow);
	display: flex;
	flex-direction: column;
	min-height: 600px;
	max-height: 80vh;
}

.section-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 15px;
	border-bottom: 1px solid var(--admin-border);
	padding-bottom: 15px;
}

.section-header h2 {
	margin: 0;
	color: var(--admin-text-primary);
	font-size: 18px;
	font-weight: 600;
}

.header-actions {
	display: flex;
	gap: 8px;
	align-items: center;
}

.search-box {
	margin-bottom: 15px;
}

.search-input {
	width: 100%;
	padding: 10px 12px;
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	background-color: var(--admin-bg-primary);
	color: var(--admin-text-primary);
	font-size: 14px;
}

.search-input::placeholder {
	color: var(--admin-text-secondary);
}

.search-input:focus {
	outline: none;
	border-color: var(--admin-text-primary);
}

.loading {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 40px 20px;
}

.spinner {
	border: 3px solid var(--admin-border);
	border-top: 3px solid var(--admin-text-primary);
	border-radius: 50%;
	width: 40px;
	height: 40px;
	animation: spin 1s linear infinite;
	margin-bottom: 10px;
}

@keyframes spin {
	0% { transform: rotate(0deg); }
	100% { transform: rotate(360deg); }
}

.items-list {
	flex: 1;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.list-item {
	padding: 12px;
	background-color: var(--admin-bg-primary);
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	cursor: pointer;
	transition: all 0.2s ease;
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.list-item:hover {
	background-color: var(--admin-hover-bg);
	border-color: var(--admin-text-primary);
}

.list-item.active {
	background-color: var(--admin-active-bg);
	border-color: var(--background-color-alt6);
	color: var(--admin-text-primary);
}

.list-item.active :deep(*) {
	color: var(--admin-text-primary);
}

.list-item-content {
	flex: 1;
	min-width: 0;
}

.item-info {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.item-name {
	color: var(--admin-text-primary);
	font-weight: 600;
	font-size: 14px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.item-actions {
	display: flex;
	gap: 8px;
	margin-left: 10px;
}

.empty-state {
	text-align: center;
	padding: 40px 20px;
	color: var(--admin-text-secondary);
}

/* Buttons */
.action-btn {
	padding: 8px 14px;
	border: none;
	border-radius: var(--border-radius-s);
	font-size: 13px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.2s ease;
	display: inline-flex;
	align-items: center;
	gap: 6px;
}

.action-btn.primary {
	background-color: var(--background-color-alt6);
	color: #fff;
}

.action-btn.secondary {
	background-color: var(--admin-hover-bg);
	color: var(--admin-text-primary);
	border: 1px solid var(--admin-border);
}

.action-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.full-width {
	width: 100%;
}

.mt-10 {
	margin-top: 10px;
}

/* Responsive */
@media (max-width: 768px) {
	.admin-list-section {
		max-height: 300px;
		min-height: auto;
		padding: 15px;
	}
}

/* Light Mode Overrides */
:root.light-mode .admin-list-section {
	background-color: #ffffff !important;
	border-color: #e0e0e0 !important;
}

:root.light-mode .list-item {
	background-color: #ffffff !important;
	border-color: #e0e0e0 !important;
}

:root.light-mode .list-item:hover {
	background-color: #f9f9f9 !important;
}

:root.light-mode .list-item.active {
	background-color: #f0f0f0 !important;
	border-color: var(--background-color-alt6) !important;
	color: #212529 !important;
}

:root.light-mode .list-item.active :deep(*) {
	color: #212529 !important;
}

:root.light-mode .search-input {
	background-color: #ffffff !important;
	border-color: #e0e0e0 !important;
}
</style>
